import { useMemo, useRef, useState } from 'react'
import { HistoryCharts, HistoryChartsHandle } from '../components/HistoryCharts'
import { EnergySummary } from '../components/EnergySummary'
import { HistoryRange, useHistory } from '../hooks/useHistory'
import { useHistoryDetail } from '../hooks/useHistoryDetail'
import type { HistoryResolution } from '../api/historyTypes'
import {
  energyBetween, fileStamp, rangeLabel, saveBlob, toCsv, ukDateString, ukMidnight, ukMidnightOf,
} from '../lib/history'
import '../styles/history.css'

/**
 * v1.3.0 — History page ('#/history').
 *
 * Presets pick the range; the API picks the resolution (30 s for a day,
 * 5-minute buckets up to 14 days, hourly to 92 days, daily beyond). Drag on
 * a chart to zoom; the energy totals and both downloads always cover exactly
 * what is on screen.
 *
 * v1.3.1 — Custom range: From/To UK dates within the last 12 months (the
 * 5-minute data's retention), applied with a button. A range ending today
 * keeps following live data like the other presets. Touch hint for phones.
 *
 * v1.3.2 — Resolution follows the zoom. Zooming into a span the API serves
 * more finely (daily -> hourly -> 5-minute -> 30 s) loads that span at the
 * finer resolution after a short pause (hooks/useHistoryDetail.ts), with the
 * coarse lines kept on screen meanwhile. Totals, CSV and image use whatever
 * is shown. Reset zoom / double-click / double-tap return to the full range.
 * Replaces the "Show 30-second detail" button.
 */

type Preset = 'today' | 'yesterday' | '7d' | '30d' | '12m' | 'day' | 'custom'

const PRESETS: { key: Preset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '12m', label: '12 months' },
]
const RAW_DAYS = 30
const ROLLUP_DAYS = 365          // 12 months of 5-minute data; the API allows up to 366 days

const RESOLUTION_TEXT: Record<HistoryResolution, string> = {
  '30s': 'readings every 30 seconds',
  '5min': '5-minute averages',
  hour: 'hourly averages',
  day: 'daily averages',
}

interface Custom { from: string; to: string }   // UK dates, YYYY-MM-DD, both inclusive

function rangeFor(preset: Preset, day: string, custom: Custom | null): HistoryRange {
  const now = Math.floor(Date.now() / 1000)
  switch (preset) {
    case 'today':     return { from: ukMidnight(now), to: 'now', key: `today-${ukDateString(now)}` }
    case 'yesterday': return { from: ukMidnight(now, -1), to: ukMidnight(now), key: `yday-${ukDateString(now)}` }
    case '7d':        return { from: now - 7 * 86400, to: 'now', key: `7d-${Math.floor(now / 300)}` }
    case '30d':       return { from: now - 30 * 86400, to: 'now', key: `30d-${Math.floor(now / 3600)}` }
    case '12m':       return { from: now - 365 * 86400, to: 'now', key: `12m-${Math.floor(now / 3600)}` }
    case 'day': {
      const from = ukMidnightOf(day)
      const to = ukMidnight(from + 43200, 1)
      return { from, to: to > now ? 'now' : to, key: `day-${day}` }
    }
    case 'custom': {
      const from = ukMidnightOf(custom!.from)
      const to = ukMidnight(ukMidnightOf(custom!.to) + 43200, 1)
      return { from, to: to > now ? 'now' : to, key: `custom-${custom!.from}-${custom!.to}` }
    }
  }
}

export function History({ systemId }: { systemId: number }) {
  const today = ukDateString(Math.floor(Date.now() / 1000))
  const earliestDay = ukDateString(Math.floor(Date.now() / 1000) - (RAW_DAYS - 1) * 86400)
  const earliestCustom = ukDateString(Math.floor(Date.now() / 1000) - ROLLUP_DAYS * 86400)
  const [preset, setPreset] = useState<Preset>('today')
  const [day, setDay] = useState(today)
  const [custom, setCustom] = useState<Custom | null>(null)          // applied
  const [draft, setDraft] = useState<Custom | null>(null)            // form open when not null
  const [view, setView] = useState<{ from: number; to: number; zoomed: boolean } | null>(null)
  const [resets, setResets] = useState(0)                            // bumped by "Reset zoom"
  const [status, setStatus] = useState('')
  const charts = useRef<HistoryChartsHandle>(null)

  const range = useMemo(() => rangeFor(preset, day, custom), [preset, day, custom])
  const { state, reload } = useHistory(systemId, range)
  // A new range or a zoom reset: the chart shows the whole range again and any detail is dropped.
  const zoomKey = `${range.key}#${resets}`

  const base = state.status === 'ready' ? state : null
  const empty = base != null && base.points.t.length === 0
  const rawFrom = base ? Date.parse(base.resp.raw_available_from) / 1000 : 0
  const { detail, loading: detailLoading } = useHistoryDetail(
    systemId, view, view?.zoomed ?? false, base?.resp.resolution ?? null, rawFrom, zoomKey, range.key)

  // What is on screen: finer detail for a zoom when loaded, otherwise the range's own data.
  const shown = base && !empty ? (detail ?? base) : null
  const zoomedIn = !!detail || !!view?.zoomed
  const visible = shown
    ? (view ?? { from: shown.points.t[0], to: shown.points.t[shown.points.t.length - 1], zoomed: false })
    : null
  const totals = shown && visible ? energyBetween(shown.points, visible.from, visible.to) : null
  // For the heading: an aggregated view's last point is a bucket start, so show where that bucket ends.
  const labelTo = shown && visible && !zoomedIn && shown.resp.resolution !== '30s' && shown.points.bucketS.length
    ? Math.min(visible.to + shown.points.bucketS[shown.points.bucketS.length - 1], Date.parse(shown.resp.range_to) / 1000)
    : visible?.to

  const choose = (p: Preset) => { setPreset(p); setView(null); setStatus(''); if (p !== 'custom') setDraft(null) }
  const resetZoom = () => { setResets((n) => n + 1); setView(null) }

  // Custom range form: opens on the dates currently shown, applies on the button.
  const openCustom = () => {
    if (draft) { setDraft(null); return }
    const clamp = (d: string) => (d < earliestCustom ? earliestCustom : d > today ? today : d)
    setDraft(visible
      ? { from: clamp(ukDateString(visible.from)), to: clamp(ukDateString(visible.to)) }
      : custom ?? { from: ukDateString(Math.floor(Date.now() / 1000) - 6 * 86400), to: today })
  }
  const draftError = !draft ? null
    : !draft.from || !draft.to ? 'Choose both dates.'
    : draft.from > draft.to ? 'The start date must be on or before the end date.'
    : draft.from < earliestCustom ? 'History goes back 12 months.'
    : draft.to > today ? "The end date can't be in the future."
    : null
  const applyCustom = () => {
    if (!draft || draftError) return
    setCustom(draft); choose('custom'); setDraft(null)
  }

  const downloadCsv = () => {
    if (!shown || !visible) return
    const csv = toCsv(shown.points, visible.from, visible.to)
    saveBlob(new Blob([csv], { type: 'text/csv' }),
      `equivolt_history_${shown.resp.resolution}_${fileStamp(visible.from)}.csv`)
    setStatus('CSV downloaded.')
  }
  const downloadPng = async () => {
    if (!shown || !visible || !charts.current) return
    const blob = await charts.current.toPng('Equivolt energy history', `${rangeLabel(visible.from, visible.to)} (UK time)`)
    if (blob) { saveBlob(blob, `equivolt_history_${fileStamp(visible.from)}.png`); setStatus('Image downloaded.') }
  }

  return (
    <div className="history-page">
      <section className="card history-card" aria-labelledby="history-title">
        <div className="card-heading history-heading">
          <h2 id="history-title">Energy history</h2>
          {visible && labelTo != null && <span className="muted small">{rangeLabel(visible.from, labelTo)}</span>}
        </div>

        <div className="history-controls">
          <div className="history-presets" role="group" aria-label="Time range">
            {PRESETS.map((p) => (
              <button key={p.key} type="button" className={`history-preset${preset === p.key ? ' active' : ''}`}
                aria-pressed={preset === p.key} onClick={() => choose(p.key)}>{p.label}</button>
            ))}
            <label className={`history-day${preset === 'day' ? ' active' : ''}`}>
              <span>Day</span>
              <input id="history-day" type="date" value={day} min={earliestDay} max={today}
                onChange={(e) => { if (e.target.value) { setDay(e.target.value); choose('day') } }} />
            </label>
            <button type="button" className={`history-preset${preset === 'custom' ? ' active' : ''}`}
              aria-pressed={preset === 'custom'} aria-expanded={draft != null} aria-controls="history-custom"
              onClick={openCustom}>Custom</button>
          </div>
          <div className="history-actions">
            <button type="button" className="btn-small" onClick={downloadCsv} disabled={!visible}>Download CSV</button>
            <button type="button" className="btn-small btn-quiet" onClick={downloadPng} disabled={!visible}>Download image</button>
          </div>
        </div>

        {draft && (
          <form id="history-custom" className="history-custom" onSubmit={(e) => { e.preventDefault(); applyCustom() }}>
            <label>
              <span>From</span>
              <input type="date" value={draft.from} min={earliestCustom} max={today}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={draft.to} min={earliestCustom} max={today}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
            </label>
            <button type="submit" className="btn-small" disabled={draftError != null}>Show</button>
            <button type="button" className="btn-small btn-quiet" onClick={() => setDraft(null)}>Cancel</button>
            {draftError && <p className="history-custom-error small" role="alert">{draftError}</p>}
          </form>
        )}

        {state.status === 'loading' && <p className="history-message muted">Loading history…</p>}
        {state.status === 'error' && (
          <div className="history-message" role="alert">
            <p>{state.message}</p>
            <button type="button" className="btn-small" onClick={() => reload()}>Try again</button>
          </div>
        )}
        {empty && (
          <p className="history-message muted">
            No readings for this period. History starts from when it was switched on for your system.
          </p>
        )}

        {shown && totals && (
          <>
            <EnergySummary totals={totals} />
            {zoomedIn && (
              <div className="history-zoombar">
                <span className="small">
                  Zoomed in · {detailLoading ? 'loading more detail…' : RESOLUTION_TEXT[shown.resp.resolution]}.
                  {' '}Totals and downloads cover what's shown.
                </span>
                <button type="button" className="btn-small btn-quiet" onClick={() => charts.current?.resetZoom()}>Reset zoom</button>
              </div>
            )}
            <HistoryCharts ref={charts} series={shown.series} rangeKey={zoomKey}
              hold={detail ? { min: detail.from, max: detail.to } : null}
              onResetZoom={resetZoom}
              onViewChange={(from, to, zoomed) => setView({ from, to, zoomed })} />
            <p className="history-hint small muted">
              <span className="hint-pointer">Drag across a chart to zoom in; double-click to zoom out. </span>
              <span className="hint-touch">Drag sideways across a chart to zoom in; double-tap to zoom out. </span>
              Zooming in shows more detail. Above zero: importing, or the battery discharging. Below zero:
              exporting, or the battery charging.
            </p>
          </>
        )}
        <p className="history-status small" aria-live="polite">{status}</p>
      </section>
    </div>
  )
}
